/**
 * Schedule date calculation utility (JS version).
 * Ported from ScheduleDateUtil.java — all names kept consistent with Java.
 */

// ==================== Enums (consistent with Java) ====================

const ScheduleIntervalType = Object.freeze({
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
});

const ScheduleMonthRepeatType = Object.freeze({
  DAY_OF_MONTH: 'DAY_OF_MONTH',
  NTH_DAY_OF_WEEK: 'NTH_DAY_OF_WEEK',
  LAST_DAY_OF_WEEK: 'LAST_DAY_OF_WEEK',
  END_OF_MONTH: 'END_OF_MONTH',
  SECOND_TO_LAST_DAY_OF_WEEK: 'SECOND_TO_LAST_DAY_OF_WEEK',
});

// Java DayOfWeek: MONDAY=1 .. SUNDAY=7 (ISO-8601)
const DayOfWeek = Object.freeze({
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
});

// Ordered array matching Java's DayOfWeek.values() iteration order
const DAY_OF_WEEK_VALUES = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

const DAY_OF_WEEK_NAMES = {
  [DayOfWeek.MONDAY]: 'Monday',
  [DayOfWeek.TUESDAY]: 'Tuesday',
  [DayOfWeek.WEDNESDAY]: 'Wednesday',
  [DayOfWeek.THURSDAY]: 'Thursday',
  [DayOfWeek.FRIDAY]: 'Friday',
  [DayOfWeek.SATURDAY]: 'Saturday',
  [DayOfWeek.SUNDAY]: 'Sunday',
};

// ==================== Internal date helpers ====================

const DT_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

function _pad(n, w) {
  return String(n).padStart(w, '0');
}

function _fmt(y, mo, d, h, mi, s) {
  return `${_pad(y, 4)}-${_pad(mo, 2)}-${_pad(d, 2)} ${_pad(h, 2)}:${_pad(mi, 2)}:${_pad(s, 2)}`;
}

function _parse(s) {
  const m = DT_RE.exec(s);
  if (!m) throw new Error(`Invalid datetime: ${s}`);
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4]),
    minute: Number(m[5]),
    second: Number(m[6]),
  };
}

function _fmtFromParts(p) {
  return _fmt(p.year, p.month, p.day, p.hour, p.minute, p.second);
}

/** Days in month (1-indexed month). */
function _daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/** JS Date.getDay() (0=Sun..6=Sat) -> DayOfWeek (1=Mon..7=Sun). */
function _jsDow(year, month, day) {
  const d = new Date(year, month - 1, day).getDay();
  return d === 0 ? 7 : d;
}

/** Add N months to {year, month, day} — clamps day to month length. */
function _addMonths(year, month, n) {
  let m = month - 1 + n;
  const y = year + Math.floor(m / 12);
  m = ((m % 12) + 12) % 12;
  return { year: y, month: m + 1 };
}

/** Compare two date-part objects: <0, 0, >0. */
function _cmpDate(a, b) {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/** Compare two datetime-part objects. */
function _cmpDateTime(a, b) {
  const d = _cmpDate(a, b);
  if (d !== 0) return d;
  if (a.hour !== b.hour) return a.hour - b.hour;
  if (a.minute !== b.minute) return a.minute - b.minute;
  return a.second - b.second;
}

/** Days between two dates (b - a). */
function _daysBetween(a, b) {
  const da = Date.UTC(a.year, a.month - 1, a.day);
  const db = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((db - da) / 86400000);
}

/** Weeks between two dates (b - a), truncated. */
function _weeksBetween(a, b) {
  return Math.floor(_daysBetween(a, b) / 7);
}

/** Months between two dates (b - a), truncated. */
function _monthsBetween(a, b) {
  return (b.year - a.year) * 12 + (b.month - a.month);
}

/** Get the Monday of the week containing {year, month, day}. */
function _previousOrSameMonday(year, month, day) {
  const dow = _jsDow(year, month, day); // 1=Mon..7=Sun
  const diff = dow - 1; // days back to Monday
  const d = new Date(year, month - 1, day - diff);
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

/** Add N days. */
function _addDays(year, month, day, n) {
  const d = new Date(year, month - 1, day + n);
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

/** Add N weeks. */
function _addWeeks(year, month, day, n) {
  return _addDays(year, month, day, n * 7);
}

/** First occurrence of dayOfWeek on or after the 1st of {year, month}. */
function _nextOrSame(year, month, targetDow) {
  const firstDow = _jsDow(year, month, 1);
  let diff = targetDow - firstDow;
  if (diff < 0) diff += 7;
  return 1 + diff;
}

/** Last occurrence of dayOfWeek in {year, month}. */
function _lastInMonth(year, month, targetDow) {
  const len = _daysInMonth(year, month);
  const lastDow = _jsDow(year, month, len);
  let diff = lastDow - targetDow;
  if (diff < 0) diff += 7;
  return len - diff;
}

// ==================== Core generation methods ====================

/**
 * DAY type: every N days from startDate.
 */
function generateDayDates(startDate, time, interval, endDate) {
  const dates = [];
  let cur = { ...startDate };
  while (_cmpDate(cur, endDate) <= 0) {
    dates.push(_fmt(cur.year, cur.month, cur.day, time.hour, time.minute, time.second));
    cur = _addDays(cur.year, cur.month, cur.day, interval);
  }
  return dates;
}

/**
 * WEEK type: every N weeks, on specified days of week.
 */
function generateWeekDates(startDate, time, interval, daysOfWeek, endDate) {
  if (!daysOfWeek || daysOfWeek.size === 0) return [];
  const dates = [];
  let ws = _previousOrSameMonday(startDate.year, startDate.month, startDate.day);
  while (_cmpDate(ws, endDate) <= 0) {
    for (const dow of DAY_OF_WEEK_VALUES) {
      if (!daysOfWeek.has(dow)) continue;
      // offset from Monday: dow-1
      const candidate = _addDays(ws.year, ws.month, ws.day, dow - 1);
      if (_cmpDate(candidate, startDate) >= 0 && _cmpDate(candidate, endDate) <= 0) {
        dates.push(_fmt(candidate.year, candidate.month, candidate.day, time.hour, time.minute, time.second));
      }
    }
    ws = _addWeeks(ws.year, ws.month, ws.day, interval);
  }
  return dates;
}

/**
 * MONTH type: every N months, supporting all five ScheduleMonthRepeatType modes.
 */
function generateMonthDates(startDate, time, interval, endDate, monthRepeatType, monthDayOfWeek) {
  let effectiveType = monthRepeatType || ScheduleMonthRepeatType.DAY_OF_MONTH;
  if (
    monthDayOfWeek === null &&
    (effectiveType === ScheduleMonthRepeatType.NTH_DAY_OF_WEEK ||
      effectiveType === ScheduleMonthRepeatType.LAST_DAY_OF_WEEK ||
      effectiveType === ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK)
  ) {
    effectiveType = ScheduleMonthRepeatType.DAY_OF_MONTH;
  }

  const dates = [];

  switch (effectiveType) {
    case ScheduleMonthRepeatType.DAY_OF_MONTH: {
      const targetDay = startDate.day;
      let cycle = 0;
      while (true) {
        const base = _addMonths(startDate.year, startDate.month, interval * cycle);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) break;
        const maxDay = _daysInMonth(base.year, base.month);
        if (targetDay <= maxDay) {
          const cur = { year: base.year, month: base.month, day: targetDay };
          if (_cmpDate(cur, endDate) > 0) break;
          dates.push(_fmt(cur.year, cur.month, cur.day, time.hour, time.minute, time.second));
        }
        cycle++;
      }
      break;
    }
    case ScheduleMonthRepeatType.NTH_DAY_OF_WEEK: {
      const ordinal = Math.floor((startDate.day - 1) / 7) + 1;
      let cycle = 0;
      while (true) {
        const base = _addMonths(startDate.year, startDate.month, interval * cycle);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) break;
        const firstOccDay = _nextOrSame(base.year, base.month, monthDayOfWeek);
        const nthDay = firstOccDay + (ordinal - 1) * 7;
        const nthMonth = new Date(base.year, base.month - 1, nthDay).getMonth() + 1;
        if (nthMonth === base.month) {
          const cur = { year: base.year, month: base.month, day: nthDay };
          if (_cmpDate(cur, endDate) > 0) break;
          dates.push(_fmt(cur.year, cur.month, cur.day, time.hour, time.minute, time.second));
        }
        cycle++;
      }
      break;
    }
    case ScheduleMonthRepeatType.LAST_DAY_OF_WEEK: {
      let cycle = 0;
      while (true) {
        const base = _addMonths(startDate.year, startDate.month, interval * cycle);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) break;
        const lastDay = _lastInMonth(base.year, base.month, monthDayOfWeek);
        const cur = { year: base.year, month: base.month, day: lastDay };
        if (_cmpDate(cur, endDate) > 0) break;
        dates.push(_fmt(cur.year, cur.month, cur.day, time.hour, time.minute, time.second));
        cycle++;
      }
      break;
    }
    case ScheduleMonthRepeatType.END_OF_MONTH: {
      let cycle = 0;
      while (true) {
        const base = _addMonths(startDate.year, startDate.month, interval * cycle);
        const lastDay = _daysInMonth(base.year, base.month);
        const cur = { year: base.year, month: base.month, day: lastDay };
        if (_cmpDate(cur, endDate) > 0) break;
        dates.push(_fmt(cur.year, cur.month, cur.day, time.hour, time.minute, time.second));
        cycle++;
      }
      break;
    }
    case ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK: {
      let cycle = 0;
      while (true) {
        const base = _addMonths(startDate.year, startDate.month, interval * cycle);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) break;
        const lastDay = _lastInMonth(base.year, base.month, monthDayOfWeek);
        const stlDay = lastDay - 7;
        const stlDate = new Date(base.year, base.month - 1, stlDay);
        if (stlDate.getMonth() + 1 === base.month) {
          const cur = { year: base.year, month: base.month, day: stlDay };
          if (_cmpDate(cur, endDate) > 0) break;
          dates.push(_fmt(cur.year, cur.month, cur.day, time.hour, time.minute, time.second));
        }
        cycle++;
      }
      break;
    }
  }
  return dates;
}

// ==================== findNext* methods ====================

function findNextDayDate(startDate, time, interval, endDate, now) {
  const db = _daysBetween(startDate, { year: now.year, month: now.month, day: now.day });
  const skipCycles = Math.max(0, Math.floor(db / interval));
  let cur = _addDays(startDate.year, startDate.month, startDate.day, skipCycles * interval);
  for (let i = 0; i < 2; i++) {
    if (_cmpDate(cur, endDate) > 0) return null;
    const candidate = { ...cur, hour: time.hour, minute: time.minute, second: time.second };
    if (_cmpDateTime(candidate, now) > 0) return _fmtFromParts(candidate);
    cur = _addDays(cur.year, cur.month, cur.day, interval);
  }
  return null;
}

function findNextWeekDate(startDate, time, interval, daysOfWeek, endDate, now) {
  if (!daysOfWeek || daysOfWeek.size === 0) return null;
  let ws = _previousOrSameMonday(startDate.year, startDate.month, startDate.day);
  const wb = _weeksBetween(ws, { year: now.year, month: now.month, day: now.day });
  const skipCycles = Math.max(0, Math.floor(wb / interval));
  ws = _addWeeks(ws.year, ws.month, ws.day, skipCycles * interval);
  for (let i = 0; i < 2; i++) {
    if (_cmpDate(ws, endDate) > 0) return null;
    for (const dow of DAY_OF_WEEK_VALUES) {
      if (!daysOfWeek.has(dow)) continue;
      const candidate = _addDays(ws.year, ws.month, ws.day, dow - 1);
      if (_cmpDate(candidate, startDate) < 0 || _cmpDate(candidate, endDate) > 0) continue;
      const dt = { ...candidate, hour: time.hour, minute: time.minute, second: time.second };
      if (_cmpDateTime(dt, now) > 0) return _fmtFromParts(dt);
    }
    ws = _addWeeks(ws.year, ws.month, ws.day, interval);
  }
  return null;
}

function findNextMonthDate(startDate, time, interval, endDate, now, monthRepeatType, monthDayOfWeek) {
  let effectiveType = monthRepeatType || ScheduleMonthRepeatType.DAY_OF_MONTH;
  if (
    monthDayOfWeek === null &&
    (effectiveType === ScheduleMonthRepeatType.NTH_DAY_OF_WEEK ||
      effectiveType === ScheduleMonthRepeatType.LAST_DAY_OF_WEEK ||
      effectiveType === ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK)
  ) {
    effectiveType = ScheduleMonthRepeatType.DAY_OF_MONTH;
  }

  const mb = _monthsBetween(startDate, { year: now.year, month: now.month, day: now.day });
  const skipCycles = Math.max(0, Math.floor(mb / interval));

  switch (effectiveType) {
    case ScheduleMonthRepeatType.DAY_OF_MONTH: {
      const targetDay = startDate.day;
      for (let i = skipCycles; i <= skipCycles + 14; i++) {
        const base = _addMonths(startDate.year, startDate.month, i * interval);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) return null;
        const maxDay = _daysInMonth(base.year, base.month);
        if (targetDay > maxDay) continue;
        const cur = { year: base.year, month: base.month, day: targetDay };
        if (_cmpDate(cur, endDate) > 0) return null;
        const candidate = { ...cur, hour: time.hour, minute: time.minute, second: time.second };
        if (_cmpDateTime(candidate, now) > 0) return _fmtFromParts(candidate);
      }
      return null;
    }
    case ScheduleMonthRepeatType.NTH_DAY_OF_WEEK: {
      const ordinal = Math.floor((startDate.day - 1) / 7) + 1;
      for (let i = skipCycles; i <= skipCycles + 2; i++) {
        const base = _addMonths(startDate.year, startDate.month, i * interval);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) return null;
        const firstOccDay = _nextOrSame(base.year, base.month, monthDayOfWeek);
        const nthDay = firstOccDay + (ordinal - 1) * 7;
        const nthMonth = new Date(base.year, base.month - 1, nthDay).getMonth() + 1;
        if (nthMonth !== base.month) continue;
        const cur = { year: base.year, month: base.month, day: nthDay };
        if (_cmpDate(cur, endDate) > 0) return null;
        const candidate = { ...cur, hour: time.hour, minute: time.minute, second: time.second };
        if (_cmpDateTime(candidate, now) > 0) return _fmtFromParts(candidate);
      }
      return null;
    }
    case ScheduleMonthRepeatType.LAST_DAY_OF_WEEK: {
      for (let i = skipCycles; i <= skipCycles + 2; i++) {
        const base = _addMonths(startDate.year, startDate.month, i * interval);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) return null;
        const lastDay = _lastInMonth(base.year, base.month, monthDayOfWeek);
        const cur = { year: base.year, month: base.month, day: lastDay };
        if (_cmpDate(cur, endDate) > 0) return null;
        const candidate = { ...cur, hour: time.hour, minute: time.minute, second: time.second };
        if (_cmpDateTime(candidate, now) > 0) return _fmtFromParts(candidate);
      }
      return null;
    }
    case ScheduleMonthRepeatType.END_OF_MONTH: {
      for (let i = skipCycles; i <= skipCycles + 2; i++) {
        const base = _addMonths(startDate.year, startDate.month, i * interval);
        const lastDay = _daysInMonth(base.year, base.month);
        const cur = { year: base.year, month: base.month, day: lastDay };
        if (_cmpDate(cur, endDate) > 0) return null;
        const candidate = { ...cur, hour: time.hour, minute: time.minute, second: time.second };
        if (_cmpDateTime(candidate, now) > 0) return _fmtFromParts(candidate);
      }
      return null;
    }
    case ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK: {
      for (let i = skipCycles; i <= skipCycles + 2; i++) {
        const base = _addMonths(startDate.year, startDate.month, i * interval);
        if (_cmpDate({ ...base, day: 1 }, endDate) > 0) return null;
        const lastDay = _lastInMonth(base.year, base.month, monthDayOfWeek);
        const stlDay = lastDay - 7;
        const stlDate = new Date(base.year, base.month - 1, stlDay);
        if (stlDate.getMonth() + 1 !== base.month) continue;
        const cur = { year: base.year, month: base.month, day: stlDay };
        if (_cmpDate(cur, endDate) > 0) return null;
        const candidate = { ...cur, hour: time.hour, minute: time.minute, second: time.second };
        if (_cmpDateTime(candidate, now) > 0) return _fmtFromParts(candidate);
      }
      return null;
    }
    default:
      return null;
  }
}

// ==================== Public API ====================

/**
 * Generate all scheduled dates from postDay to endDate (inclusive).
 *
 * @param {string} postDay - "yyyy-MM-dd HH:mm:ss"
 * @param {number} interval - repeat interval
 * @param {string} type - ScheduleIntervalType value
 * @param {Set<number>|null} daysOfWeek - Set of DayOfWeek values (WEEK type only)
 * @param {string} endDate - "yyyy-MM-dd HH:mm:ss" (time part ignored)
 * @param {string|null} monthRepeatType - ScheduleMonthRepeatType value
 * @param {number|null} monthDayOfWeek - DayOfWeek value
 * @returns {string[]}
 */
function generateScheduleDates(postDay, interval, type, daysOfWeek, endDate, monthRepeatType, monthDayOfWeek) {
  if (postDay === null || endDate === null || interval <= 0 || type === null) return [];

  const startDt = _parse(postDay);
  const endDt = _parse(endDate);
  const time = { hour: startDt.hour, minute: startDt.minute, second: startDt.second };
  const startDate = { year: startDt.year, month: startDt.month, day: startDt.day };
  const endLocalDate = { year: endDt.year, month: endDt.month, day: endDt.day };

  switch (type) {
    case ScheduleIntervalType.DAY:
      return generateDayDates(startDate, time, interval, endLocalDate);
    case ScheduleIntervalType.WEEK:
      return generateWeekDates(startDate, time, interval, daysOfWeek, endLocalDate);
    case ScheduleIntervalType.MONTH:
      return generateMonthDates(startDate, time, interval, endLocalDate, monthRepeatType, monthDayOfWeek);
    default:
      return [];
  }
}

/**
 * Get the next scheduled date after a reference time.
 *
 * @param {string} postDay
 * @param {number} interval
 * @param {string} type - ScheduleIntervalType
 * @param {Set<number>|null} daysOfWeek
 * @param {string} endDate
 * @param {string|null} monthRepeatType - ScheduleMonthRepeatType
 * @param {number|null} monthDayOfWeek - DayOfWeek
 * @param {string|null} now - reference time (null = current time)
 * @returns {string|null}
 */
function getNextScheduleDate(postDay, interval, type, daysOfWeek, endDate, monthRepeatType, monthDayOfWeek, now) {
  if (postDay === null || endDate === null || interval <= 0 || type === null) return null;

  let nowDt;
  if (now !== null) {
    nowDt = _parse(now);
  } else {
    const d = new Date();
    nowDt = {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    };
  }

  const postDt = _parse(postDay);
  const endDt = _parse(endDate);
  const time = { hour: postDt.hour, minute: postDt.minute, second: postDt.second };
  const startDate = { year: postDt.year, month: postDt.month, day: postDt.day };
  const endLocalDate = { year: endDt.year, month: endDt.month, day: endDt.day };

  switch (type) {
    case ScheduleIntervalType.DAY:
      return findNextDayDate(startDate, time, interval, endLocalDate, nowDt);
    case ScheduleIntervalType.WEEK:
      return findNextWeekDate(startDate, time, interval, daysOfWeek, endLocalDate, nowDt);
    case ScheduleIntervalType.MONTH:
      return findNextMonthDate(startDate, time, interval, endLocalDate, nowDt, monthRepeatType, monthDayOfWeek);
    default:
      return null;
  }
}

// ==================== getMonthRepeatOptions ====================

function getOrdinalName(ordinal) {
  switch (ordinal) {
    case 1:
      return 'first';
    case 2:
      return 'second';
    case 3:
      return 'third';
    case 4:
      return 'fourth';
    default:
      return '';
  }
}

function capitalizeDayName(dayOfWeek) {
  return DAY_OF_WEEK_NAMES[dayOfWeek] || '';
}

/**
 * Generate all applicable month repeat options for a given date.
 *
 * @param {string} postDay - "yyyy-MM-dd HH:mm:ss"
 * @returns {Array<{type: string, label: string, dayOfWeek: number|null, ordinal: number}>}
 */
function getMonthRepeatOptions(postDay) {
  if (postDay === null) return [];
  let dt;
  try {
    dt = _parse(postDay);
  } catch (e) {
    return [];
  }

  const options = [];
  const dayOfMonth = dt.day;
  const dayOfWeek = _jsDow(dt.year, dt.month, dt.day);
  const dayName = capitalizeDayName(dayOfWeek);
  const lengthOfMonth = _daysInMonth(dt.year, dt.month);

  // 1. DAY_OF_MONTH
  options.push({
    type: ScheduleMonthRepeatType.DAY_OF_MONTH,
    label: `On day ${dayOfMonth}`,
    dayOfWeek: null,
    ordinal: 0,
  });

  // 2. NTH_DAY_OF_WEEK if ordinal <= 4
  const ordinal = Math.floor((dayOfMonth - 1) / 7) + 1;
  if (ordinal <= 4) {
    options.push({
      type: ScheduleMonthRepeatType.NTH_DAY_OF_WEEK,
      label: `On the ${getOrdinalName(ordinal)} ${dayName}`,
      dayOfWeek,
      ordinal,
    });
  }

  // 3. LAST_DAY_OF_WEEK if date + 7 exceeds month
  if (dayOfMonth + 7 > lengthOfMonth) {
    options.push({
      type: ScheduleMonthRepeatType.LAST_DAY_OF_WEEK,
      label: `On the last ${dayName}`,
      dayOfWeek,
      ordinal: 0,
    });
  }

  // 4. END_OF_MONTH if last day
  if (dayOfMonth === lengthOfMonth) {
    options.push({
      type: ScheduleMonthRepeatType.END_OF_MONTH,
      label: 'At the end of Month',
      dayOfWeek: null,
      ordinal: 0,
    });
  }

  // 5. SECOND_TO_LAST_DAY_OF_WEEK
  if (dayOfMonth + 14 > lengthOfMonth && !(dayOfMonth + 7 > lengthOfMonth)) {
    options.push({
      type: ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK,
      label: `On the second-to-last ${dayName}`,
      dayOfWeek,
      ordinal: 0,
    });
  }

  return options;
}

// ==================== Comprehensive Tests ====================

let _passed = 0;
let _failed = 0;

function check(testName, condition) {
  if (condition) {
    _passed++;
    console.log(`  PASS: ${testName}`);
  } else {
    _failed++;
    console.log(`  FAIL: ${testName}`);
  }
}

function hasType(opts, type) {
  return opts.some((o) => o.type === type);
}

function labelOf(opts, type) {
  const o = opts.find((o) => o.type === type);
  return o ? o.label : null;
}

function runTests() {
  console.log('============================================================');
  console.log('  ScheduleDateUtil Comprehensive Tests (JS)');
  console.log('============================================================\n');

  // ===================== PART 0: DAY and WEEK basic tests =====================

  // DAY: every 3 days
  console.log('=== DAY: every 3 days ===');
  const dayDates = generateScheduleDates(
    '2026-03-18 10:00:00',
    3,
    ScheduleIntervalType.DAY,
    null,
    '2026-03-30 23:59:59',
    null,
    null
  );
  dayDates.forEach((d) => console.log(`  ${d}`));
  check('DAY: 5 dates', dayDates.length === 5);
  check('DAY: Mar 18', dayDates.includes('2026-03-18 10:00:00'));
  check('DAY: Mar 21', dayDates.includes('2026-03-21 10:00:00'));
  check('DAY: Mar 24', dayDates.includes('2026-03-24 10:00:00'));
  check('DAY: Mar 27', dayDates.includes('2026-03-27 10:00:00'));
  check('DAY: Mar 30', dayDates.includes('2026-03-30 10:00:00'));

  // WEEK: every 1 week on Mon/Wed/Fri
  console.log('\n=== WEEK: every 1 week on Mon/Wed/Fri ===');
  const weekDays = new Set([DayOfWeek.MONDAY, DayOfWeek.WEDNESDAY, DayOfWeek.FRIDAY]);
  const weekDates = generateScheduleDates(
    '2026-03-18 10:00:00',
    1,
    ScheduleIntervalType.WEEK,
    weekDays,
    '2026-04-03 23:59:59',
    null,
    null
  );
  weekDates.forEach((d) => console.log(`  ${d}`));
  check('WEEK: 8 dates', weekDates.length === 8);
  check('WEEK: Mar 18 (Wed)', weekDates.includes('2026-03-18 10:00:00'));
  check('WEEK: Mar 20 (Fri)', weekDates.includes('2026-03-20 10:00:00'));
  check('WEEK: Mar 23 (Mon)', weekDates.includes('2026-03-23 10:00:00'));
  check('WEEK: Mar 25 (Wed)', weekDates.includes('2026-03-25 10:00:00'));
  check('WEEK: Mar 27 (Fri)', weekDates.includes('2026-03-27 10:00:00'));
  check('WEEK: Mar 30 (Mon)', weekDates.includes('2026-03-30 10:00:00'));
  check('WEEK: Apr 1 (Wed)', weekDates.includes('2026-04-01 10:00:00'));
  check('WEEK: Apr 3 (Fri)', weekDates.includes('2026-04-03 10:00:00'));

  // WEEK: every 2 weeks on Tue/Thu
  console.log('\n=== WEEK: every 2 weeks on Tue/Thu ===');
  const weekDays2 = new Set([DayOfWeek.TUESDAY, DayOfWeek.THURSDAY]);
  const weekDates2 = generateScheduleDates(
    '2026-03-17 09:00:00',
    2,
    ScheduleIntervalType.WEEK,
    weekDays2,
    '2026-04-15 23:59:59',
    null,
    null
  );
  weekDates2.forEach((d) => console.log(`  ${d}`));
  check('WEEK2: Mar 17 (Tue)', weekDates2.includes('2026-03-17 09:00:00'));
  check('WEEK2: Mar 19 (Thu)', weekDates2.includes('2026-03-19 09:00:00'));
  check('WEEK2: Mar 31 (Tue)', weekDates2.includes('2026-03-31 09:00:00'));
  check('WEEK2: Apr 2 (Thu)', weekDates2.includes('2026-04-02 09:00:00'));
  check('WEEK2: Apr 14 (Tue)', weekDates2.includes('2026-04-14 09:00:00'));

  // DAY: every 1 day, single day range
  console.log('\n=== DAY: every 1 day, single day ===');
  const singleDay = generateScheduleDates(
    '2026-05-01 08:00:00',
    1,
    ScheduleIntervalType.DAY,
    null,
    '2026-05-01 23:59:59',
    null,
    null
  );
  check('DAY single: 1 date', singleDay.length === 1);
  check('DAY single: May 1', singleDay.includes('2026-05-01 08:00:00'));

  // ===================== PART 1: getMonthRepeatOptions - 5 Cases =====================

  // Case A: Day of Month (2023-06-13, Tuesday)
  console.log('\n=== Case A: Day of Month - 2023-06-13 (Tuesday) ===');
  const caseA = getMonthRepeatOptions('2023-06-13 10:00:00');
  caseA.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Case A: has 2 options', caseA.length === 2);
  check('Case A: DAY_OF_MONTH label', labelOf(caseA, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 13');
  check(
    'Case A: NTH_DAY_OF_WEEK label',
    labelOf(caseA, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK) === 'On the second Tuesday'
  );
  check('Case A: no LAST_DAY_OF_WEEK', !hasType(caseA, ScheduleMonthRepeatType.LAST_DAY_OF_WEEK));
  check('Case A: no END_OF_MONTH', !hasType(caseA, ScheduleMonthRepeatType.END_OF_MONTH));

  // Case C: Last day of week (2023-07-28, Friday)
  console.log('\n=== Case C: Last day of week - 2023-07-28 (Friday) ===');
  const caseC = getMonthRepeatOptions('2023-07-28 10:00:00');
  caseC.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Case C: has 3 options', caseC.length === 3);
  check('Case C: DAY_OF_MONTH label', labelOf(caseC, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 28');
  check(
    'Case C: NTH_DAY_OF_WEEK label',
    labelOf(caseC, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK) === 'On the fourth Friday'
  );
  check(
    'Case C: LAST_DAY_OF_WEEK label',
    labelOf(caseC, ScheduleMonthRepeatType.LAST_DAY_OF_WEEK) === 'On the last Friday'
  );

  // Case D: Last day of month (2023-07-31, Monday)
  console.log('\n=== Case D: Last day of month - 2023-07-31 (Monday) ===');
  const caseD = getMonthRepeatOptions('2023-07-31 10:00:00');
  caseD.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Case D: has 3 options', caseD.length === 3);
  check('Case D: DAY_OF_MONTH label', labelOf(caseD, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 31');
  check('Case D: END_OF_MONTH label', labelOf(caseD, ScheduleMonthRepeatType.END_OF_MONTH) === 'At the end of Month');
  check(
    'Case D: LAST_DAY_OF_WEEK label',
    labelOf(caseD, ScheduleMonthRepeatType.LAST_DAY_OF_WEEK) === 'On the last Monday'
  );
  check('Case D: no NTH_DAY_OF_WEEK (ordinal=5)', !hasType(caseD, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK));

  // Case E: Second-to-last day of week (2023-07-23, Sunday)
  console.log('\n=== Case E: Second-to-last day of week - 2023-07-23 (Sunday) ===');
  const caseE = getMonthRepeatOptions('2023-07-23 10:00:00');
  caseE.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Case E: has 3 options', caseE.length === 3);
  check('Case E: DAY_OF_MONTH label', labelOf(caseE, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 23');
  check(
    'Case E: NTH_DAY_OF_WEEK label',
    labelOf(caseE, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK) === 'On the fourth Sunday'
  );
  check(
    'Case E: SECOND_TO_LAST label',
    labelOf(caseE, ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK) === 'On the second-to-last Sunday'
  );

  // ===================== PART 2: getMonthRepeatOptions - Special dates =====================

  // Feb 28, 2025 (non-leap year, month-end, Friday)
  console.log('\n=== Special: Feb 28, 2025 (non-leap, month-end, Friday) ===');
  const feb28 = getMonthRepeatOptions('2025-02-28 10:00:00');
  feb28.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Feb28: has 4 options', feb28.length === 4);
  check('Feb28: DAY_OF_MONTH', labelOf(feb28, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 28');
  check('Feb28: NTH_DAY_OF_WEEK', labelOf(feb28, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK) === 'On the fourth Friday');
  check('Feb28: LAST_DAY_OF_WEEK', labelOf(feb28, ScheduleMonthRepeatType.LAST_DAY_OF_WEEK) === 'On the last Friday');
  check('Feb28: END_OF_MONTH', labelOf(feb28, ScheduleMonthRepeatType.END_OF_MONTH) === 'At the end of Month');

  // Feb 29, 2024 (leap year, month-end, Thursday)
  console.log('\n=== Special: Feb 29, 2024 (leap year, month-end, Thursday) ===');
  const feb29 = getMonthRepeatOptions('2024-02-29 10:00:00');
  feb29.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Feb29: has 3 options', feb29.length === 3);
  check('Feb29: DAY_OF_MONTH', labelOf(feb29, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 29');
  check('Feb29: END_OF_MONTH', labelOf(feb29, ScheduleMonthRepeatType.END_OF_MONTH) === 'At the end of Month');
  check('Feb29: LAST_DAY_OF_WEEK', labelOf(feb29, ScheduleMonthRepeatType.LAST_DAY_OF_WEEK) === 'On the last Thursday');
  check('Feb29: no NTH_DAY_OF_WEEK (ordinal=5)', !hasType(feb29, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK));

  // Mar 1, 2026 (first day, Sunday, ordinal=1)
  console.log('\n=== Special: Mar 1, 2026 (Sunday, ordinal=1) ===');
  const mar1 = getMonthRepeatOptions('2026-03-01 10:00:00');
  mar1.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Mar1: has 2 options', mar1.length === 2);
  check('Mar1: DAY_OF_MONTH', labelOf(mar1, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 1');
  check('Mar1: NTH_DAY_OF_WEEK', labelOf(mar1, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK) === 'On the first Sunday');

  // Mar 24, 2026 (Tuesday, 4th Tuesday + second-to-last)
  console.log('\n=== Special: Mar 24, 2026 (Tuesday, 4th + second-to-last) ===');
  const mar24 = getMonthRepeatOptions('2026-03-24 10:00:00');
  mar24.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Mar24: has 3 options', mar24.length === 3);
  check('Mar24: DAY_OF_MONTH', labelOf(mar24, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 24');
  check('Mar24: NTH_DAY_OF_WEEK', labelOf(mar24, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK) === 'On the fourth Tuesday');
  check(
    'Mar24: SECOND_TO_LAST',
    labelOf(mar24, ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK) === 'On the second-to-last Tuesday'
  );

  // Mar 31, 2026 (Tuesday, month-end, 5th Tuesday)
  console.log('\n=== Special: Mar 31, 2026 (Tuesday, month-end, ordinal=5) ===');
  const mar31 = getMonthRepeatOptions('2026-03-31 10:00:00');
  mar31.forEach((o) => console.log(`  ${o.type} -> ${o.label}`));
  check('Mar31: has 3 options', mar31.length === 3);
  check('Mar31: DAY_OF_MONTH', labelOf(mar31, ScheduleMonthRepeatType.DAY_OF_MONTH) === 'On day 31');
  check('Mar31: LAST_DAY_OF_WEEK', labelOf(mar31, ScheduleMonthRepeatType.LAST_DAY_OF_WEEK) === 'On the last Tuesday');
  check('Mar31: END_OF_MONTH', labelOf(mar31, ScheduleMonthRepeatType.END_OF_MONTH) === 'At the end of Month');
  check('Mar31: no NTH_DAY_OF_WEEK (ordinal=5)', !hasType(mar31, ScheduleMonthRepeatType.NTH_DAY_OF_WEEK));

  // ===================== PART 3: generateScheduleDates - All ScheduleMonthRepeatType modes =====================

  // DAY_OF_MONTH: day 31, every 1 month, Jan-Jun 2026
  console.log('\n=== Part 3a: DAY_OF_MONTH day 31, every 1 month, Jan-Jun 2026 ===');
  const domDates = generateScheduleDates(
    '2026-01-31 08:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2026-06-30 23:59:59',
    ScheduleMonthRepeatType.DAY_OF_MONTH,
    null
  );
  domDates.forEach((d) => console.log(`  ${d}`));
  check('DAY_OF_MONTH: 3 dates (Jan,Mar,May)', domDates.length === 3);
  check('DAY_OF_MONTH: Jan 31', domDates.includes('2026-01-31 08:00:00'));
  check('DAY_OF_MONTH: Mar 31', domDates.includes('2026-03-31 08:00:00'));
  check('DAY_OF_MONTH: May 31', domDates.includes('2026-05-31 08:00:00'));

  // NTH_DAY_OF_WEEK: 2nd Tuesday, every 1 month, Mar-Jul 2026
  console.log('\n=== Part 3b: NTH_DAY_OF_WEEK 2nd Tuesday, every 1 month, Mar-Jul 2026 ===');
  const nthDates = generateScheduleDates(
    '2026-03-10 09:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2026-07-31 23:59:59',
    ScheduleMonthRepeatType.NTH_DAY_OF_WEEK,
    DayOfWeek.TUESDAY
  );
  nthDates.forEach((d) => console.log(`  ${d}`));
  check('NTH_DAY_OF_WEEK: 5 dates', nthDates.length === 5);
  check('NTH: Mar 10', nthDates.includes('2026-03-10 09:00:00'));
  check('NTH: Apr 14', nthDates.includes('2026-04-14 09:00:00'));
  check('NTH: May 12', nthDates.includes('2026-05-12 09:00:00'));
  check('NTH: Jun 9', nthDates.includes('2026-06-09 09:00:00'));
  check('NTH: Jul 14', nthDates.includes('2026-07-14 09:00:00'));

  // LAST_DAY_OF_WEEK: last Friday, every 1 month, Jan-Jun 2026
  console.log('\n=== Part 3c: LAST_DAY_OF_WEEK last Friday, every 1 month, Jan-Jun 2026 ===');
  const lastFriDates = generateScheduleDates(
    '2026-01-30 10:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2026-06-30 23:59:59',
    ScheduleMonthRepeatType.LAST_DAY_OF_WEEK,
    DayOfWeek.FRIDAY
  );
  lastFriDates.forEach((d) => console.log(`  ${d}`));
  check('LAST_DAY_OF_WEEK: 6 dates', lastFriDates.length === 6);
  check('LastFri: Jan 30', lastFriDates.includes('2026-01-30 10:00:00'));
  check('LastFri: Feb 27', lastFriDates.includes('2026-02-27 10:00:00'));
  check('LastFri: Mar 27', lastFriDates.includes('2026-03-27 10:00:00'));
  check('LastFri: Apr 24', lastFriDates.includes('2026-04-24 10:00:00'));
  check('LastFri: May 29', lastFriDates.includes('2026-05-29 10:00:00'));
  check('LastFri: Jun 26', lastFriDates.includes('2026-06-26 10:00:00'));

  // END_OF_MONTH: every 1 month, Jan-Jun 2026
  console.log('\n=== Part 3d: END_OF_MONTH every 1 month, Jan-Jun 2026 ===');
  const eomDates = generateScheduleDates(
    '2026-01-31 12:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2026-06-30 23:59:59',
    ScheduleMonthRepeatType.END_OF_MONTH,
    null
  );
  eomDates.forEach((d) => console.log(`  ${d}`));
  check('END_OF_MONTH: 6 dates', eomDates.length === 6);
  check('EOM: Jan 31', eomDates.includes('2026-01-31 12:00:00'));
  check('EOM: Feb 28', eomDates.includes('2026-02-28 12:00:00'));
  check('EOM: Mar 31', eomDates.includes('2026-03-31 12:00:00'));
  check('EOM: Apr 30', eomDates.includes('2026-04-30 12:00:00'));
  check('EOM: May 31', eomDates.includes('2026-05-31 12:00:00'));
  check('EOM: Jun 30', eomDates.includes('2026-06-30 12:00:00'));

  // SECOND_TO_LAST_DAY_OF_WEEK: second-to-last Sunday, every 1 month, Jan-Jun 2026
  console.log('\n=== Part 3e: SECOND_TO_LAST_DAY_OF_WEEK 2nd-to-last Sunday, every 1 month, Jan-Jun 2026 ===');
  const stlDates = generateScheduleDates(
    '2026-01-18 14:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2026-06-30 23:59:59',
    ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK,
    DayOfWeek.SUNDAY
  );
  stlDates.forEach((d) => console.log(`  ${d}`));
  check('SECOND_TO_LAST: 6 dates', stlDates.length === 6);
  check('STL: Jan 18', stlDates.includes('2026-01-18 14:00:00'));
  check('STL: Feb 15', stlDates.includes('2026-02-15 14:00:00'));
  check('STL: Mar 22', stlDates.includes('2026-03-22 14:00:00'));
  check('STL: Apr 19', stlDates.includes('2026-04-19 14:00:00'));
  check('STL: May 24', stlDates.includes('2026-05-24 14:00:00'));
  check('STL: Jun 21', stlDates.includes('2026-06-21 14:00:00'));

  // Backward compatibility: null monthRepeatType, day 15, every 1 month
  console.log('\n=== Part 3f: Backward compat - null monthRepeatType, day 15 ===');
  const bcDates = generateScheduleDates(
    '2026-01-15 08:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2026-06-30 23:59:59',
    null,
    null
  );
  bcDates.forEach((d) => console.log(`  ${d}`));
  check('Backward compat: 6 dates', bcDates.length === 6);
  check('BC: Jan 15', bcDates.includes('2026-01-15 08:00:00'));
  check('BC: Feb 15', bcDates.includes('2026-02-15 08:00:00'));
  check('BC: Mar 15', bcDates.includes('2026-03-15 08:00:00'));
  check('BC: Apr 15', bcDates.includes('2026-04-15 08:00:00'));
  check('BC: May 15', bcDates.includes('2026-05-15 08:00:00'));
  check('BC: Jun 15', bcDates.includes('2026-06-15 08:00:00'));

  // ===================== PART 4: generateScheduleDates - Leap year Feb 29 =====================

  // END_OF_MONTH every 1 month, Jan-Mar 2024 (leap year)
  console.log('\n=== Part 4a: END_OF_MONTH Jan-Mar 2024 (leap year) ===');
  const leapEom = generateScheduleDates(
    '2024-01-31 10:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2024-03-31 23:59:59',
    ScheduleMonthRepeatType.END_OF_MONTH,
    null
  );
  leapEom.forEach((d) => console.log(`  ${d}`));
  check('Leap END_OF_MONTH: 3 dates', leapEom.length === 3);
  check('Leap EOM: Jan 31', leapEom.includes('2024-01-31 10:00:00'));
  check('Leap EOM: Feb 29', leapEom.includes('2024-02-29 10:00:00'));
  check('Leap EOM: Mar 31', leapEom.includes('2024-03-31 10:00:00'));

  // DAY_OF_MONTH day 29, every 1 month, Jan-Apr 2025 (non-leap)
  console.log('\n=== Part 4b: DAY_OF_MONTH day 29, Jan-Apr 2025 (non-leap, Feb skipped) ===');
  const dom29 = generateScheduleDates(
    '2025-01-29 10:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    '2025-04-30 23:59:59',
    ScheduleMonthRepeatType.DAY_OF_MONTH,
    null
  );
  dom29.forEach((d) => console.log(`  ${d}`));
  check('DOM29: 3 dates (Feb skipped)', dom29.length === 3);
  check('DOM29: Jan 29', dom29.includes('2025-01-29 10:00:00'));
  check('DOM29: Mar 29', dom29.includes('2025-03-29 10:00:00'));
  check('DOM29: Apr 29', dom29.includes('2025-04-29 10:00:00'));

  // ===================== PART 5: findNextMonthDate - each mode generates non-empty results =====================

  console.log('\n=== Part 5: findNextMonthDate - each mode over 12-month span ===');
  const futureStart = '2030-01-01 10:00:00';
  const futureEnd = '2030-12-31 23:59:59';

  const domFuture = generateScheduleDates(
    futureStart,
    1,
    ScheduleIntervalType.MONTH,
    null,
    futureEnd,
    ScheduleMonthRepeatType.DAY_OF_MONTH,
    null
  );
  check('findNext DAY_OF_MONTH: non-empty', domFuture.length > 0);
  console.log(`  DAY_OF_MONTH dates: ${domFuture.length}`);

  const nthFuture = generateScheduleDates(
    '2030-01-06 10:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    futureEnd,
    ScheduleMonthRepeatType.NTH_DAY_OF_WEEK,
    DayOfWeek.MONDAY
  );
  check('findNext NTH_DAY_OF_WEEK: non-empty', nthFuture.length > 0);
  console.log(`  NTH_DAY_OF_WEEK dates: ${nthFuture.length}`);

  const lastFuture = generateScheduleDates(
    futureStart,
    1,
    ScheduleIntervalType.MONTH,
    null,
    futureEnd,
    ScheduleMonthRepeatType.LAST_DAY_OF_WEEK,
    DayOfWeek.WEDNESDAY
  );
  check('findNext LAST_DAY_OF_WEEK: non-empty', lastFuture.length > 0);
  console.log(`  LAST_DAY_OF_WEEK dates: ${lastFuture.length}`);

  const eomFuture = generateScheduleDates(
    futureStart,
    1,
    ScheduleIntervalType.MONTH,
    null,
    futureEnd,
    ScheduleMonthRepeatType.END_OF_MONTH,
    null
  );
  check('findNext END_OF_MONTH: non-empty', eomFuture.length > 0);
  console.log(`  END_OF_MONTH dates: ${eomFuture.length}`);

  const stlFuture = generateScheduleDates(
    futureStart,
    1,
    ScheduleIntervalType.MONTH,
    null,
    futureEnd,
    ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK,
    DayOfWeek.SUNDAY
  );
  check('findNext SECOND_TO_LAST: non-empty', stlFuture.length > 0);
  console.log(`  SECOND_TO_LAST dates: ${stlFuture.length}`);

  // ===================== PART 6: End-to-End Case A-E (getOptions -> generateDates) =====================

  const e2eEnd = '2024-06-30 23:59:59';

  // Case A: 2023-06-13 (Tuesday)
  console.log('\n=== E2E Case A: 2023-06-13 (Tuesday) - each option generates dates to 2024-06-30 ===');
  const e2eA = getMonthRepeatOptions('2023-06-13 18:00:00');
  for (const opt of e2eA) {
    const dates = generateScheduleDates(
      '2023-06-13 18:00:00',
      1,
      ScheduleIntervalType.MONTH,
      null,
      e2eEnd,
      opt.type,
      opt.dayOfWeek
    );
    console.log(`  [${opt.label}] (${dates.length} dates):`);
    dates.forEach((d) => console.log(`    ${d}`));
  }
  check('E2E Case A: 2 options', e2eA.length === 2);

  // Case C: 2023-07-28 (Friday)
  console.log('\n=== E2E Case C: 2023-07-28 (Friday) - each option generates dates to 2024-06-30 ===');
  const e2eC = getMonthRepeatOptions('2023-07-28 18:00:00');
  for (const opt of e2eC) {
    const dates = generateScheduleDates(
      '2023-07-28 18:00:00',
      1,
      ScheduleIntervalType.MONTH,
      null,
      e2eEnd,
      opt.type,
      opt.dayOfWeek
    );
    console.log(`  [${opt.label}] (${dates.length} dates):`);
    dates.forEach((d) => console.log(`    ${d}`));
  }
  check('E2E Case C: 3 options', e2eC.length === 3);

  // Case D: 2023-07-31 (Monday)
  console.log('\n=== E2E Case D: 2023-07-31 (Monday) - each option generates dates to 2024-06-30 ===');
  const e2eD = getMonthRepeatOptions('2023-07-31 18:00:00');
  for (const opt of e2eD) {
    const dates = generateScheduleDates(
      '2023-07-31 18:00:00',
      1,
      ScheduleIntervalType.MONTH,
      null,
      e2eEnd,
      opt.type,
      opt.dayOfWeek
    );
    console.log(`  [${opt.label}] (${dates.length} dates):`);
    dates.forEach((d) => console.log(`    ${d}`));
  }
  check('E2E Case D: 3 options', e2eD.length === 3);

  // Case E: 2023-07-23 (Sunday)
  console.log('\n=== E2E Case E: 2023-07-23 (Sunday) - each option generates dates to 2024-06-30 ===');
  const e2eE = getMonthRepeatOptions('2023-07-23 18:00:00');
  for (const opt of e2eE) {
    const dates = generateScheduleDates(
      '2023-07-23 18:00:00',
      1,
      ScheduleIntervalType.MONTH,
      null,
      e2eEnd,
      opt.type,
      opt.dayOfWeek
    );
    console.log(`  [${opt.label}] (${dates.length} dates):`);
    dates.forEach((d) => console.log(`    ${d}`));
  }
  check('E2E Case E: 3 options', e2eE.length === 3);

  // ===================== PART 7: interval=2 and interval=3 for all ScheduleMonthRepeatType =====================

  const intEnd = '2027-12-31 23:59:59';

  // interval=2, DAY_OF_MONTH day 31
  console.log('\n=== Interval=2: DAY_OF_MONTH day 31, Jan 2026 - Dec 2027 ===');
  const int2dom = generateScheduleDates(
    '2026-01-31 10:00:00',
    2,
    ScheduleIntervalType.MONTH,
    null,
    intEnd,
    ScheduleMonthRepeatType.DAY_OF_MONTH,
    null
  );
  int2dom.forEach((d) => console.log(`  ${d}`));
  check('Int2 DAY_OF_MONTH: Jan 31', int2dom.includes('2026-01-31 10:00:00'));
  check('Int2 DAY_OF_MONTH: Mar 31', int2dom.includes('2026-03-31 10:00:00'));
  check('Int2 DAY_OF_MONTH: May 31', int2dom.includes('2026-05-31 10:00:00'));
  check('Int2 DAY_OF_MONTH: Jul 31', int2dom.includes('2026-07-31 10:00:00'));
  check('Int2 DAY_OF_MONTH: no Sep (30 days)', !int2dom.includes('2026-09-30 10:00:00'));
  check('Int2 DAY_OF_MONTH: Nov 30 skipped', !int2dom.includes('2026-11-30 10:00:00'));

  // interval=2, LAST_DAY_OF_WEEK Friday
  console.log('\n=== Interval=2: LAST_DAY_OF_WEEK Friday, Jan 2026 - Dec 2026 ===');
  const int2last = generateScheduleDates(
    '2026-01-30 10:00:00',
    2,
    ScheduleIntervalType.MONTH,
    null,
    '2026-12-31 23:59:59',
    ScheduleMonthRepeatType.LAST_DAY_OF_WEEK,
    DayOfWeek.FRIDAY
  );
  int2last.forEach((d) => console.log(`  ${d}`));
  check('Int2 LAST_FRI: 6 dates', int2last.length === 6);
  check('Int2 LAST_FRI: Jan 30', int2last.includes('2026-01-30 10:00:00'));
  check('Int2 LAST_FRI: Mar 27', int2last.includes('2026-03-27 10:00:00'));
  check('Int2 LAST_FRI: May 29', int2last.includes('2026-05-29 10:00:00'));
  check('Int2 LAST_FRI: Jul 31', int2last.includes('2026-07-31 10:00:00'));
  check('Int2 LAST_FRI: Sep 25', int2last.includes('2026-09-25 10:00:00'));
  check('Int2 LAST_FRI: Nov 27', int2last.includes('2026-11-27 10:00:00'));

  // interval=3, NTH_DAY_OF_WEEK 2nd Tuesday
  console.log('\n=== Interval=3: NTH_DAY_OF_WEEK 2nd Tuesday, Mar 2026 - Dec 2027 ===');
  const int3nth = generateScheduleDates(
    '2026-03-10 09:00:00',
    3,
    ScheduleIntervalType.MONTH,
    null,
    intEnd,
    ScheduleMonthRepeatType.NTH_DAY_OF_WEEK,
    DayOfWeek.TUESDAY
  );
  int3nth.forEach((d) => console.log(`  ${d}`));
  check('Int3 NTH: Mar 10 2026', int3nth.includes('2026-03-10 09:00:00'));
  check('Int3 NTH: Jun 9 2026', int3nth.includes('2026-06-09 09:00:00'));
  check('Int3 NTH: Sep 8 2026', int3nth.includes('2026-09-08 09:00:00'));
  check('Int3 NTH: Dec 8 2026', int3nth.includes('2026-12-08 09:00:00'));
  check('Int3 NTH: 8 dates total', int3nth.length === 8);

  // interval=2, END_OF_MONTH
  console.log('\n=== Interval=2: END_OF_MONTH, Jan 2026 - Dec 2026 ===');
  const int2eom = generateScheduleDates(
    '2026-01-31 12:00:00',
    2,
    ScheduleIntervalType.MONTH,
    null,
    '2026-12-31 23:59:59',
    ScheduleMonthRepeatType.END_OF_MONTH,
    null
  );
  int2eom.forEach((d) => console.log(`  ${d}`));
  check('Int2 EOM: 6 dates', int2eom.length === 6);
  check('Int2 EOM: Jan 31', int2eom.includes('2026-01-31 12:00:00'));
  check('Int2 EOM: Mar 31', int2eom.includes('2026-03-31 12:00:00'));
  check('Int2 EOM: May 31', int2eom.includes('2026-05-31 12:00:00'));
  check('Int2 EOM: Jul 31', int2eom.includes('2026-07-31 12:00:00'));
  check('Int2 EOM: Sep 30', int2eom.includes('2026-09-30 12:00:00'));
  check('Int2 EOM: Nov 30', int2eom.includes('2026-11-30 12:00:00'));

  // interval=3, SECOND_TO_LAST_DAY_OF_WEEK Sunday
  console.log('\n=== Interval=3: SECOND_TO_LAST Sunday, Jan 2026 - Dec 2027 ===');
  const int3stl = generateScheduleDates(
    '2026-01-18 14:00:00',
    3,
    ScheduleIntervalType.MONTH,
    null,
    intEnd,
    ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK,
    DayOfWeek.SUNDAY
  );
  int3stl.forEach((d) => console.log(`  ${d}`));
  check('Int3 STL: Jan 18 2026', int3stl.includes('2026-01-18 14:00:00'));
  check('Int3 STL: Apr 19 2026', int3stl.includes('2026-04-19 14:00:00'));
  check('Int3 STL: Jul 19 2026', int3stl.includes('2026-07-19 14:00:00'));
  check('Int3 STL: 8 dates total', int3stl.length === 8);

  // ===================== PART 8: getNextScheduleDate with fixed now for all types =====================

  const fixedNow = '2026-06-15 12:00:00';
  const nextEnd = '2027-12-31 23:59:59';

  // DAY: every 3 days from 2026-03-18
  console.log('\n=== Next: DAY every 3 days (now=2026-06-15) ===');
  const nextDay = getNextScheduleDate(
    '2026-03-18 10:00:00',
    3,
    ScheduleIntervalType.DAY,
    null,
    nextEnd,
    null,
    null,
    fixedNow
  );
  console.log(`  Next: ${nextDay}`);
  check('Next DAY: 2026-06-16', nextDay === '2026-06-16 10:00:00');

  // WEEK: every 1 week Mon/Wed/Fri from 2026-03-18
  console.log('\n=== Next: WEEK every 1 week Mon/Wed/Fri (now=2026-06-15) ===');
  const nwDays = new Set([DayOfWeek.MONDAY, DayOfWeek.WEDNESDAY, DayOfWeek.FRIDAY]);
  const nextWeek = getNextScheduleDate(
    '2026-03-18 10:00:00',
    1,
    ScheduleIntervalType.WEEK,
    nwDays,
    nextEnd,
    null,
    null,
    fixedNow
  );
  console.log(`  Next: ${nextWeek}`);
  check('Next WEEK: 2026-06-17', nextWeek === '2026-06-17 10:00:00');

  // MONTH DAY_OF_MONTH: day 31, every 1 month
  console.log('\n=== Next: MONTH DAY_OF_MONTH day 31 (now=2026-06-15) ===');
  const nextDom = getNextScheduleDate(
    '2026-01-31 08:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.DAY_OF_MONTH,
    null,
    fixedNow
  );
  console.log(`  Next: ${nextDom}`);
  check('Next DOM31: 2026-07-31', nextDom === '2026-07-31 08:00:00');

  // MONTH NTH_DAY_OF_WEEK: 2nd Tuesday, every 1 month
  console.log('\n=== Next: MONTH NTH 2nd Tuesday (now=2026-06-15) ===');
  const nextNth = getNextScheduleDate(
    '2026-03-10 09:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.NTH_DAY_OF_WEEK,
    DayOfWeek.TUESDAY,
    fixedNow
  );
  console.log(`  Next: ${nextNth}`);
  check('Next NTH: 2026-07-14', nextNth === '2026-07-14 09:00:00');

  // MONTH LAST_DAY_OF_WEEK: last Friday, every 1 month
  console.log('\n=== Next: MONTH LAST Friday (now=2026-06-15) ===');
  const nextLast = getNextScheduleDate(
    '2026-01-30 10:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.LAST_DAY_OF_WEEK,
    DayOfWeek.FRIDAY,
    fixedNow
  );
  console.log(`  Next: ${nextLast}`);
  check('Next LAST_FRI: 2026-06-26', nextLast === '2026-06-26 10:00:00');

  // MONTH END_OF_MONTH: every 1 month
  console.log('\n=== Next: MONTH END_OF_MONTH (now=2026-06-15) ===');
  const nextEom = getNextScheduleDate(
    '2026-01-31 12:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.END_OF_MONTH,
    null,
    fixedNow
  );
  console.log(`  Next: ${nextEom}`);
  check('Next EOM: 2026-06-30', nextEom === '2026-06-30 12:00:00');

  // MONTH SECOND_TO_LAST_DAY_OF_WEEK Sunday: every 1 month
  console.log('\n=== Next: MONTH SECOND_TO_LAST Sunday (now=2026-06-15) ===');
  const nextStl = getNextScheduleDate(
    '2026-01-18 14:00:00',
    1,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.SECOND_TO_LAST_DAY_OF_WEEK,
    DayOfWeek.SUNDAY,
    fixedNow
  );
  console.log(`  Next: ${nextStl}`);
  check('Next STL: 2026-06-21', nextStl === '2026-06-21 14:00:00');

  // interval=2 tests with fixed now
  const fixedNow2 = '2026-04-01 00:00:00';

  // interval=2, LAST_DAY_OF_WEEK Friday
  console.log('\n=== Next: interval=2 LAST Friday (now=2026-04-01) ===');
  const nextLast2 = getNextScheduleDate(
    '2026-01-30 10:00:00',
    2,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.LAST_DAY_OF_WEEK,
    DayOfWeek.FRIDAY,
    fixedNow2
  );
  console.log(`  Next: ${nextLast2}`);
  check('Next Int2 LAST_FRI: 2026-05-29', nextLast2 === '2026-05-29 10:00:00');

  // interval=2, END_OF_MONTH
  console.log('\n=== Next: interval=2 END_OF_MONTH (now=2026-04-01) ===');
  const nextEom2 = getNextScheduleDate(
    '2026-01-31 12:00:00',
    2,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.END_OF_MONTH,
    null,
    fixedNow2
  );
  console.log(`  Next: ${nextEom2}`);
  check('Next Int2 EOM: 2026-05-31', nextEom2 === '2026-05-31 12:00:00');

  // interval=3, NTH 2nd Tuesday
  console.log('\n=== Next: interval=3 NTH 2nd Tuesday (now=2026-04-01) ===');
  const nextNth3 = getNextScheduleDate(
    '2026-03-10 09:00:00',
    3,
    ScheduleIntervalType.MONTH,
    null,
    nextEnd,
    ScheduleMonthRepeatType.NTH_DAY_OF_WEEK,
    DayOfWeek.TUESDAY,
    fixedNow2
  );
  console.log(`  Next: ${nextNth3}`);
  check('Next Int3 NTH: 2026-06-09', nextNth3 === '2026-06-09 09:00:00');

  // ===================== SUMMARY =====================
  console.log('\n============================================================');
  console.log(`  RESULTS: ${_passed} passed, ${_failed} failed, ${_passed + _failed} total`);
  console.log('============================================================');
  if (_failed > 0) {
    console.log('  *** SOME TESTS FAILED ***');
    if (typeof process !== 'undefined') process.exit(1);
  } else {
    console.log('  ALL TESTS PASSED');
  }
}

// Run tests when executed directly
// runTests();

export { generateScheduleDates };
